import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { useOneClickStore } from './oneClickStore';

export interface BookLiquidityPreference { enabled: boolean; cap: number | null }
const KEY = 'galactus:bookLiquiditySettings';
const EMPTY: BookLiquidityPreference = { enabled: false, cap: null };
export const validLiquidityCap = (n: unknown): n is number =>
  typeof n === 'number' && Number.isSafeInteger(n) && n > 0;
function read(): Record<string, BookLiquidityPreference> {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, BookLiquidityPreference>;
    return Object.fromEntries(Object.entries(raw).map(([id, value]) => {
      const cap = validLiquidityCap(value?.cap) ? value.cap : null;
      return [id, { enabled: value?.enabled === true && cap !== null, cap }];
    }));
  } catch { return {}; }
}
interface State {
  traders: Record<string, BookLiquidityPreference>;
  update: (userId: string, next: BookLiquidityPreference) => void;
}
export const useBookLiquiditySettings = create<State>((set) => ({
  traders: read(),
  update: (userId, next) => {
    if (!userId) return;
    const cap = validLiquidityCap(next.cap) ? next.cap : null;
    // Merge the stored map first so saving one trader cannot erase another
    // trader's settings from a tab opened before their change.
    const traders = { ...read(), [userId]: { enabled: next.enabled && cap !== null, cap } };
    try { localStorage.setItem(KEY, JSON.stringify(traders)); } catch { /* in-memory preference remains usable */ }
    set({ traders });
  },
}));
export const bookLiquidityPreference = (userId: string | null | undefined) =>
  userId ? useBookLiquiditySettings.getState().traders[userId] ?? EMPTY : EMPTY;
if (typeof window !== 'undefined') window.addEventListener('storage', e => {
  if (e.key === KEY || e.key === null) useBookLiquiditySettings.setState({ traders: read() });
});

/** One displayed level, never cumulative depth or dollars. No rounding up. */
export function cappedLevelContracts(size: number, cap: number | null): number | null {
  if (!validLiquidityCap(cap) || !Number.isFinite(size) || size < 1) return null;
  return Math.min(Math.floor(size), cap);
}


export interface BookLiquiditySeed {
  sequence: number;
  ownerId: string;
  orgId: string | null;
  ticker: string;
  side: 'yes' | 'no';
  count: number;
}
let sequence = 0;
/** Capture preferences and displayed depth at the click, before any fallback
 * disarms one-click. A render, quote update or later preference change is not
 * another sizing command. */
export function captureBookLiquidity(ticker: string | null | undefined, side: 'yes' | 'no', levelContracts: number): BookLiquiditySeed | null {
  const user = useAuthStore.getState().user;
  const pref = bookLiquidityPreference(user?.id);
  if (!user || !ticker || !pref.enabled || useOneClickStore.getState().armed) return null;
  const count = cappedLevelContracts(levelContracts, pref.cap);
  return count === null ? null : { sequence: ++sequence, ownerId: user.id, orgId: user.orgId ?? null, ticker, side, count };
}
